import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {MutationObserver | null} */
  #chatObserver = null;

  /** @type {ResizeObserver | null} */
  #chatResizeObserver = null;

  /** @type {MutationObserver | null} */
  #typebotDiscoveryObserver = null;

  /** @type {MutationObserver | null} */
  #typebotStateObserver = null;

  /** @type {ResizeObserver | null} */
  #typebotResizeObserver = null;

  /** @type {IntersectionObserver | null} */
  #typebotIntersectionObserver = null;

  /** @type {HTMLElement | null} */
  #typebotContainer = null;

  /** @type {string} */
  #typebotOriginalBottom = '';

  /** @type {number | null} */
  #typebotCollisionFrame = null;

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();

    this.#setupChatState(signal);
    this.#setupTypebotCollision(signal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#chatObserver?.disconnect();
    this.#chatResizeObserver?.disconnect();
    this.#typebotDiscoveryObserver?.disconnect();
    this.#typebotStateObserver?.disconnect();
    this.#typebotResizeObserver?.disconnect();
    this.#typebotIntersectionObserver?.disconnect();
    this.#restoreTypebotBottom();
    if (this.#typebotCollisionFrame !== null) cancelAnimationFrame(this.#typebotCollisionFrame);
    this.#abortController.abort();
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = () => {
    if (this.dataset.hasVariants === 'true') {
      this.#scrollToVariantPicker();
      return;
    }

    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles product select events (variant selected and updated)
   * @param {ProductSelectEvent} event - The product select event
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Update variant ID from the event detail (variant:selected part)
    const { optionValueId } = event.detail ?? {};
    if (optionValueId) {
      this.dataset.currentVariantId = optionValueId;
    }

    // Wait for the promise to resolve with variant update data
    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html, productId, resource: variant } = detail;

        if (productId && productId !== this.dataset.productId) return;

        // Get the new sticky add to cart HTML from the server response
        const newStickyAddToCart = /** @type {HTMLElement | null} */ (html.querySelector('sticky-add-to-cart'));
        if (!newStickyAddToCart) return;

        const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
        if (!newStickyBar) return;

        // Store current visibility state before morphing
        const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
        const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

        // Morph the entire sticky bar content
        morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

        // Restore visibility state after morphing
        this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
        this.dataset.variantAvailable = variantAvailable;

        // Update the dataset attributes with new variant info
        if (variant && variant.id) {
          this.dataset.currentVariantId = variant.id;
        }

        // Re-cache the target add to cart button after morphing
        const productForm = this.#getProductForm();
        if (productForm) {
          this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
        }

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
        // Restore the current quantity display if needed
        this.#updateButtonText();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[sticky-add-to-cart] Event promise rejected:', error);
      });
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CartLinesUpdateEvent | CartErrorEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag only after the cart operation's promise settles,
    // not when the event is first dispatched (before the HTTP request completes).
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet = 'false';
      }
    };

    // CartLinesUpdateEvent has a promise; CartErrorEvent does not (error already happened).
    if ('promise' in event && event.promise instanceof Promise) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
    this.#scheduleTypebotCollisionCheck();
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
    this.#restoreTypebotBottom();
  }

  // Helper methods
  /**
   * Tracks whether Shopify Inbox is installed and whether its panel is open.
   * The launcher gets reserved space; the expanded panel temporarily hides the bar.
   * @param {AbortSignal} signal
   */
  #setupChatState(signal) {
    const connect = () => {
      if (signal.aborted) return;
      const chat = document.querySelector('shopify-chat');
      if (!chat) return;

      const syncState = () => {
        this.dataset.chatActive = 'true';
        this.dataset.chatOpen = chat.hasAttribute('open') ? 'true' : 'false';

        const rect = chat.getBoundingClientRect();
        let clearance = 96;
        if (rect.height >= 24 && rect.height <= 180 && rect.top > window.innerHeight / 2) {
          clearance = Math.min(190, Math.max(clearance, Math.ceil(window.innerHeight - rect.top + 12)));
        }
        this.style.setProperty('--sticky-chat-clearance', `${clearance}px`);
        this.#scheduleTypebotCollisionCheck();
      };

      syncState();
      this.#chatObserver?.disconnect();
      this.#chatObserver = new MutationObserver(syncState);
      this.#chatObserver.observe(chat, { attributes: true, attributeFilter: ['open'] });
      if ('ResizeObserver' in window) {
        this.#chatResizeObserver?.disconnect();
        this.#chatResizeObserver = new ResizeObserver(syncState);
        this.#chatResizeObserver.observe(chat);
      }
      window.addEventListener('resize', syncState, { signal });
    };

    if (customElements.get('shopify-chat')) connect();
    else customElements.whenDefined('shopify-chat').then(connect);
  }

  /**
   * Finds Typebot's floating container inside its open shadow root and observes
   * both widgets. The custom bottom offset is only applied after a real overlap.
   * @param {AbortSignal} signal
   */
  #setupTypebotCollision(signal) {
    const connect = () => {
      if (signal.aborted) return;

      const host = document.querySelector('typebot-bubble');
      const root = host?.shadowRoot;
      if (!root) return;

      const container = Array.from(root.querySelectorAll('[style]')).find(
        (element) =>
          element instanceof HTMLElement && element.style.getPropertyValue('--container-bottom').trim() !== ''
      );

      if (!(container instanceof HTMLElement)) return;

      if (container !== this.#typebotContainer) {
        this.#restoreTypebotBottom();
        this.#typebotContainer = container;
        this.#typebotOriginalBottom = container.style.getPropertyValue('--container-bottom').trim();

        this.#typebotResizeObserver?.disconnect();
        if ('ResizeObserver' in window) {
          this.#typebotResizeObserver = new ResizeObserver(() => this.#scheduleTypebotCollisionCheck());
          this.#typebotResizeObserver.observe(this.refs.stickyBar);
          this.#typebotResizeObserver.observe(container);
        }

        this.#typebotIntersectionObserver?.disconnect();
        this.#typebotIntersectionObserver = new IntersectionObserver(() => this.#scheduleTypebotCollisionCheck(), {
          threshold: [0, 0.25, 0.5, 0.75, 1],
        });
        this.#typebotIntersectionObserver.observe(this.refs.stickyBar);
        this.#typebotIntersectionObserver.observe(container);

        this.#typebotStateObserver?.disconnect();
        this.#typebotStateObserver = new MutationObserver(() => {
          if (!container.isConnected) connect();
          this.#scheduleTypebotCollisionCheck();
        });
        this.#typebotStateObserver.observe(root, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['aria-pressed', 'class', 'data-state', 'open'],
        });
      }

      this.#scheduleTypebotCollisionCheck();
    };

    this.#typebotDiscoveryObserver = new MutationObserver(connect);
    this.#typebotDiscoveryObserver.observe(document.documentElement, { childList: true, subtree: true });

    connect();
    customElements.whenDefined('typebot-bubble').then(connect);
    window.addEventListener('resize', this.#scheduleTypebotCollisionCheck, { signal });
  }

  /**
   * Batches layout reads so ResizeObserver and Typebot state changes cannot
   * trigger repeated calculations in the same frame.
   */
  #scheduleTypebotCollisionCheck = () => {
    if (this.#typebotCollisionFrame !== null) return;

    this.#typebotCollisionFrame = requestAnimationFrame(() => {
      this.#typebotCollisionFrame = null;
      this.#syncTypebotBottom();
    });
  };

  /**
   * Compares the rendered rectangles at Typebot's original position. If they
   * intersect, the bubble is moved just above the sticky bar; otherwise its own
   * configured --container-bottom value is restored.
   */
  #syncTypebotBottom() {
    const container = this.#typebotContainer;
    const { stickyBar } = this.refs;
    if (!container?.isConnected) return;

    this.#restoreTypebotBottom();
    if (!this.#isStuck || stickyBar.dataset.stuck !== 'true') return;

    const stickyRect = stickyBar.getBoundingClientRect();
    const bubbleRect = container.getBoundingClientRect();
    if (stickyRect.width === 0 || stickyRect.height === 0 || bubbleRect.width === 0 || bubbleRect.height === 0) return;

    const overlapsHorizontally = stickyRect.left < bubbleRect.right && stickyRect.right > bubbleRect.left;
    const overlapsVertically = stickyRect.top < bubbleRect.bottom && stickyRect.bottom > bubbleRect.top;
    if (!overlapsHorizontally || !overlapsVertically) return;

    const originalBottom = Number.parseFloat(getComputedStyle(container).getPropertyValue('--container-bottom')) || 0;
    const collisionGap = 12;
    const requiredBottom = Math.ceil(window.innerHeight - stickyRect.top + collisionGap);
    container.style.setProperty('--container-bottom', `${Math.max(originalBottom, requiredBottom)}px`);
  }

  /** Restores Typebot's configured position when there is no collision. */
  #restoreTypebotBottom() {
    if (!this.#typebotContainer) return;

    if (this.#typebotOriginalBottom) {
      this.#typebotContainer.style.setProperty('--container-bottom', this.#typebotOriginalBottom);
    } else {
      this.#typebotContainer.style.removeProperty('--container-bottom');
    }
  }

  /**
   * Returns shoppers with configurable products to the product option controls.
   * The sticky CTA deliberately does not add a preselected option silently.
   */
  #scrollToVariantPicker() {
    const productId = this.dataset.productId;
    const sectionElement = this.closest('.shopify-section');
    const variantPicker =
      sectionElement?.querySelector(`variant-picker[data-product-id="${productId}"]`) ??
      document.querySelector(`variant-picker[data-product-id="${productId}"]`);

    this.#hideStickyBar();

    if (!variantPicker) {
      sectionElement?.querySelector('[data-testid="product-information-details"]')?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'center',
      });
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    variantPicker.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

    const firstControl = variantPicker.querySelector(
      'select:not([disabled]), input:not([disabled]), button:not([disabled])'
    );
    window.setTimeout(
      () => {
        if (firstControl instanceof HTMLElement) firstControl.focus({ preventScroll: true });
      },
      reduceMotion ? 0 : 450
    );
  }

  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    if (this.dataset.hasVariants === 'true') {
      quantityDisplay.hidden = true;
      return;
    }

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}
